import React, { useRef } from 'react';
import styled, { css } from 'styled-components';
import { PanelLayoutContext } from '.';
import { useLayoutScrollHandler } from 'features/useScrollHandler';
import { useIsSandboxed } from 'components/preview-tabs/PreviewPanel';

interface StickyPanelSidebarProps {
  children: React.ReactNode;
  className?: string;
  width?: number;
}

export function StickyPanelSidebar({
  children,
  className,
  width = 360,
}: StickyPanelSidebarProps) {
  const { scrolled, mainPanelRef, panelRef, headerHeight } =
    React.useContext(PanelLayoutContext);
  const sandboxed = useIsSandboxed();

  const elRef = useRef<HTMLDivElement>(null);

  const handleScroll = React.useCallback(() => {
    const el = elRef.current;
    if (el && !scrolled) {
      const { top } = mainPanelRef.current?.getBoundingClientRect();
      el.style.top = `${top}px`;
    }
  }, [scrolled, mainPanelRef]);

  const calculateSidebarTop = React.useCallback(() => {
    const el = elRef.current;
    if (el) {
      if (scrolled && !sandboxed) {
        el.style.top = `${headerHeight}px`;
      } else {
        const { top } = mainPanelRef.current?.getBoundingClientRect() || {
          top: 0,
        };
        el.style.top = `${top}px`;
      }
    }
  }, [scrolled, sandboxed, headerHeight, mainPanelRef]);

  React.useEffect(() => {
    if (!panelRef.current) return;

    const observer = new MutationObserver(calculateSidebarTop);
    observer.observe(panelRef.current, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    // Initial measurement
    calculateSidebarTop();

    return () => observer.disconnect();
  }, [calculateSidebarTop, panelRef]);

  useLayoutScrollHandler(handleScroll);

  return (
    <>
      <StyledStickyPanelSidebar
        className={className}
        ref={elRef}
        $width={width}
      >
        {children}
      </StyledStickyPanelSidebar>
    </>
  );
}

type StyledStickyPanelSidebarProps = {
  $width?: number;
};

const StyledStickyPanelSidebar = styled.div<StyledStickyPanelSidebarProps>`
  @media screen and (min-width: 1164px) {
    position: fixed;
    overflow: auto;
    bottom: 0;
    ${({ $width }) =>
      $width &&
      css`
        width: 100%;
        max-width: ${$width}px;
      `}
  }
`;
